/**
 * Vittoria Retailer Finder
 * Simplified fork of the V4 store locator: no tiers/weighting, no marker
 * clustering — just the nearest N retailers to the searched location,
 * closest first, optionally capped to a maximum distance.
 *
 * Reads config from window.retailerFinderSettings (set by the section).
 */

document.addEventListener('DOMContentLoaded', function () {
  if (typeof google === 'undefined') {
    console.error('Google Maps API is not loaded');
    return;
  }

  // ---- Settings ----------------------------------------------------------
  const settings = window.retailerFinderSettings || {};

  const API_URL = settings.apiUrl;
  const DEFAULT_LAT = parseFloat(settings.defaultLat) || 52.4862;   // UK centre-ish fallback
  const DEFAULT_LNG = parseFloat(settings.defaultLng) || -1.8904;
  const DEFAULT_ZOOM = 9;
  const SEARCH_ZOOM = settings.searchZoom || 11;
  const MARKER_ZOOM = settings.markerZoom || 13;

  const DISTANCE_UNIT = settings.distanceUnit || 'miles';
  const DISTANCE_MULTIPLIER = DISTANCE_UNIT === 'kilometers' ? 1.60934 : 1;
  const DISTANCE_LABEL = DISTANCE_UNIT === 'kilometers' ? 'km' : 'miles';

  const MAX_RESULTS = parseInt(settings.maxResults, 10) || 10;
  // Radius cap is expressed in the chosen display unit. 0 (or less) = no cap.
  const RADIUS_CAP = parseFloat(settings.radiusCap) || 0;

  const MARKER_COLOR = settings.markerColor || '#288066';
  const USER_MARKER_COLOR = settings.userMarkerColor || '#15403e';

  const MAX_RECENT_SEARCHES = 5;
  const MAP_ANIMATION_DURATION = settings.mapAnimationDuration || 750;

  const LOCATION_TYPE_ZOOM = {
    country: 5, administrative_area_level_1: 7, administrative_area_level_2: 9,
    locality: 12, sublocality: 13, neighborhood: 14, route: 15, street_address: 16,
    postal_code: 12, default: 12
  };

  // Text strings
  const LOADING_TEXT = settings.loadingText || 'Loading retailers…';
  const NO_RESULTS_TEXT = settings.noResultsText || 'No retailers found in this area.';
  const ERROR_LOADING_TEXT = settings.errorLoadingText || 'Error loading retailers. Please try again.';
  const LOCATION_ERROR_TEXT = settings.locationErrorText || 'Unable to get your location. Please enter a location manually.';
  const GEOLOCATION_NOT_SUPPORTED_TEXT = settings.geolocationNotSupportedText || 'Geolocation is not supported by your browser. Please enter a location manually.';
  const YOUR_LOCATION_TEXT = settings.yourLocationText || 'Your location';
  const SEARCH_PROMPT_TEXT = settings.searchPromptText || 'Enter a town or postcode above to find your nearest retailers.';

  // ---- DOM ---------------------------------------------------------------
  const searchInput = document.getElementById('retailer-search');
  const useMyLocationBtn = document.getElementById('use-my-location-btn');
  const retailerList = document.getElementById('retailer-list');
  const mapElement = document.getElementById('retailer-map');

  if (!mapElement || !retailerList) {
    console.error('Retailer finder: required elements missing');
    return;
  }

  // ---- State -------------------------------------------------------------
  let map, markers = [], bounds, geocoder, searchBox, activeMarker = null;
  let retailers = [], allRetailers = [], userPosition = null, userMarker = null;
  let isInitialLoad = true, mapAnimationInProgress = false;
  // Details used to expand inline per card; they now open in an InfoWindow on
  // the pin (desktop) or a dialog (mobile, where the map is short and stacked
  // below the list, leaving an InfoWindow no room to breathe).
  let infoWindow = null, selectedIndex = null;
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const modal = document.getElementById('retailer-modal');
  const modalBody = document.getElementById('retailer-modal-body');

  const RETAILERS_CACHE_KEY = 'vittoria_retailer_finder_retailers';
  const RETAILERS_CACHE_TIMESTAMP_KEY = 'vittoria_retailer_finder_timestamp';
  const CACHE_EXPIRY_MINUTES = 60;
  const PIN_SVG_PATH = 'M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z';

  // ---- Init --------------------------------------------------------------
  initMap();
  initAutocomplete();

  if (useMyLocationBtn) useMyLocationBtn.addEventListener('click', getUserLocation);

  function initAutocomplete() {
    if (!searchInput || !google.maps.places) return;
    try {
      const countryCodes = settings.countryCodes || ['GB', 'GG', 'JE', 'IM'];
      searchBox = new google.maps.places.Autocomplete(searchInput, {
        types: ['geocode'],
        componentRestrictions: { country: countryCodes }
      });
      searchBox.addListener('place_changed', function () {
        const place = searchBox.getPlace();
        if (!place.geometry) return;
        handlePlaceSelection(place);
      });
      // Also allow Enter to geocode free text when no suggestion is picked.
      searchInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (!searchBox.getPlace() || !searchBox.getPlace().geometry) handleSearch();
        }
      });
    } catch (error) {
      console.error('Error initializing autocomplete:', error);
    }
  }

  function initMap() {
    try {
      map = new google.maps.Map(mapElement, {
        center: { lat: DEFAULT_LAT, lng: DEFAULT_LNG },
        zoom: DEFAULT_ZOOM,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
        styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }]
      });
      geocoder = new google.maps.Geocoder();
      bounds = new google.maps.LatLngBounds();
      userPosition = { lat: DEFAULT_LAT, lng: DEFAULT_LNG };
      isInitialLoad = true;
      loadRetailers();
    } catch (error) {
      console.error('Error initializing map:', error);
      mapElement.innerHTML = 'Error loading map. Please refresh the page.';
    }
  }

  function addUserMarker(position) {
    if (userMarker) userMarker.setMap(null);
    userMarker = new google.maps.Marker({
      position, map, zIndex: 1000, title: YOUR_LOCATION_TEXT,
      animation: google.maps.Animation.DROP,
      icon: {
        path: google.maps.SymbolPath.CIRCLE, fillColor: USER_MARKER_COLOR, fillOpacity: 0.9,
        strokeColor: '#FFFFFF', strokeWeight: 3, scale: 9
      }
    });
  }

  function getUserLocation() {
    if (!navigator.geolocation) {
      retailerList.innerHTML = `<div class="retailer-finder__message">${GEOLOCATION_NOT_SUPPORTED_TEXT}</div>`;
      return;
    }
    retailerList.innerHTML = `<div class="retailer-finder__loading">Getting your location…</div>`;
    navigator.geolocation.getCurrentPosition(
      position => {
        isInitialLoad = false;
        userPosition = { lat: position.coords.latitude, lng: position.coords.longitude };
        animateMapMove(userPosition, SEARCH_ZOOM);
        addUserMarker(userPosition);
        updateRetailers();
        fitToResults();
        scrollListToTop();
      },
      error => {
        console.error('Error getting location', error);
        retailerList.innerHTML = `<div class="retailer-finder__message">${LOCATION_ERROR_TEXT}</div>`;
      }
    );
  }

  function handlePlaceSelection(place) {
    isInitialLoad = false;
    userPosition = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
    saveRecentSearch(place.formatted_address, userPosition);
    addUserMarker(userPosition);
    applySearch(place.geometry.viewport, place.types);
  }

  function handleSearch() {
    const query = searchInput.value.trim();
    if (!query) return;
    retailerList.innerHTML = `<div class="retailer-finder__loading">Searching for location…</div>`;
    geocoder.geocode({ address: query, componentRestrictions: { country: 'GB' } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        isInitialLoad = false;
        userPosition = {
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng()
        };
        saveRecentSearch(query, userPosition);
        addUserMarker(userPosition);
        applySearch(results[0].geometry.viewport, results[0].types);
      } else {
        console.error('Geocode failed:', status);
        retailerList.innerHTML = `<div class="retailer-finder__message">Location not found. Please try a different search.</div>`;
      }
    });
  }

  // Shared post-search behaviour: move the map, refresh results, fit bounds.
  function applySearch(viewport, types) {
    animateMapMove(userPosition, zoomForTypes(types));
    if (allRetailers.length > 0) {
      updateRetailers();
      fitToResults(viewport);
      scrollListToTop();
    } else {
      loadRetailers();
    }
  }

  function zoomForTypes(types) {
    if (types && types.length) {
      for (const type of types) {
        if (LOCATION_TYPE_ZOOM[type]) return LOCATION_TYPE_ZOOM[type];
      }
    }
    return SEARCH_ZOOM;
  }

  function scrollListToTop() { if (retailerList) retailerList.scrollTop = 0; }

  function animateMapMove(position, targetZoom) {
    if (!map) return;
    map.panTo(position);
    if (targetZoom !== undefined) map.setZoom(targetZoom);
  }

  // Fit the map to the user + returned results so all pins are visible.
  function fitToResults(viewport) {
    if (!userPosition) return;
    if (retailers.length === 0) {
      if (viewport) map.fitBounds(viewport);
      else { map.setCenter(userPosition); map.setZoom(SEARCH_ZOOM); }
      return;
    }
    const b = new google.maps.LatLngBounds();
    b.extend(new google.maps.LatLng(userPosition.lat, userPosition.lng));
    retailers.forEach(r => b.extend(new google.maps.LatLng(parseFloat(r.latitude), parseFloat(r.longitude))));
    map.fitBounds(b, { top: 40, right: 40, bottom: 40, left: 40 });
  }

  // ---- Data --------------------------------------------------------------
  async function loadRetailers() {
    const cached = getCachedRetailers();
    if (cached) {
      allRetailers = cached;
      updateRetailers();
      fetchRetailersFromAPI(true); // refresh in background
    } else {
      fetchRetailersFromAPI();
    }
  }

  async function fetchRetailersFromAPI(background) {
    if (!API_URL) {
      retailerList.innerHTML = `<div class="retailer-finder__error">${ERROR_LOADING_TEXT}</div>`;
      return;
    }
    try {
      if (!background) retailerList.innerHTML = `<div class="retailer-finder__loading">${LOADING_TEXT}</div>`;
      const response = await fetch(API_URL);
      if (!response.ok) throw new Error(`API request failed with status ${response.status}`);
      const data = await response.json();
      if (!Array.isArray(data)) throw new Error('API did not return an array of retailers');
      allRetailers = data;
      cacheRetailers(allRetailers);
      updateRetailers();
    } catch (error) {
      console.error('Error fetching retailers:', error);
      if (!background) retailerList.innerHTML = `<div class="retailer-finder__error">${ERROR_LOADING_TEXT}</div>`;
    }
  }

  function getCachedRetailers() {
    const cachedData = localStorage.getItem(RETAILERS_CACHE_KEY);
    const timestamp = localStorage.getItem(RETAILERS_CACHE_TIMESTAMP_KEY);
    if (!cachedData || !timestamp) return null;
    if (Date.now() - timestamp > CACHE_EXPIRY_MINUTES * 60 * 1000) {
      localStorage.removeItem(RETAILERS_CACHE_KEY);
      localStorage.removeItem(RETAILERS_CACHE_TIMESTAMP_KEY);
      return null;
    }
    try { return JSON.parse(cachedData); }
    catch (e) { console.error('Error parsing cached retailers:', e); return null; }
  }

  function cacheRetailers(list) {
    try {
      localStorage.setItem(RETAILERS_CACHE_KEY, JSON.stringify(list));
      localStorage.setItem(RETAILERS_CACHE_TIMESTAMP_KEY, Date.now().toString());
    } catch (e) { console.error('Error caching retailers:', e); }
  }

  // ---- The one behavioural change: nearest N, closest first, radius cap --
  function updateRetailers() {
    // A new search invalidates whatever was open.
    closeDetail();
    selectedIndex = null;

    if (!userPosition || isInitialLoad) {
      retailers = [];
    } else {
      retailers = allRetailers
        .map(r => ({
          ...r,
          distance: calculateDistance(
            userPosition.lat, userPosition.lng,
            parseFloat(r.latitude), parseFloat(r.longitude)
          ) * DISTANCE_MULTIPLIER
        }))
        .filter(r => !isNaN(r.distance))
        .filter(r => RADIUS_CAP <= 0 || r.distance <= RADIUS_CAP)
        .sort((a, b) => a.distance - b.distance)
        .slice(0, MAX_RESULTS);
    }

    displayRetailers();
    updateMarkers();
  }

  function calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 3959; // miles
    const dLat = toRad(lat2 - lat1), dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }
  function toRad(v) { return v * Math.PI / 180; }

  // ---- Render list -------------------------------------------------------
  function displayRetailers() {
    retailerList.innerHTML = '';
    renderPrintList();
    if (isInitialLoad) {
      retailerList.innerHTML = `<div class="retailer-finder__message">${SEARCH_PROMPT_TEXT}</div>`;
      return;
    }
    if (retailers.length === 0) {
      retailerList.innerHTML = `<div class="retailer-finder__message">${NO_RESULTS_TEXT}</div>`;
      return;
    }
    retailers.forEach((retailer, index) => addRetailerToList(retailer, index));
  }

  // The print stylesheet used to reveal each card's inline details. With the
  // details moved onto the map, a printed page would have carried names and
  // distances only — so mirror the full details into a screen-hidden block
  // that only the print stylesheet shows.
  function renderPrintList() {
    const printList = document.getElementById('retailer-print-list');
    if (!printList) return;
    printList.innerHTML = retailers
      .map(r => buildDetailHTML(r, { headingLevel: 'h3' }))
      .join('');
  }

  // A card is now a plain selector: one click picks the retailer and its
  // details open on the map. A real <button> gives keyboard and screen-reader
  // support for free, so the card carries no interactive descendants — every
  // link (tel/mailto/website/directions) lives in the detail view instead,
  // which is also what keeps the button valid HTML.
  function addRetailerToList(retailer, index) {
    const el = document.createElement('button');
    el.type = 'button';
    el.className = 'retailer-item';
    el.dataset.index = index;
    el.setAttribute('aria-label', `${retailer.name}. Show details.`);

    const distanceHTML = retailer.distance !== undefined
      ? `<span class="retailer-distances"><span class="retailer-distance">${retailer.distance.toFixed(1)} ${DISTANCE_LABEL}</span></span>`
      : '';

    el.innerHTML = `
      <span class="retailer-header">
        <span class="retailer-main-info">
          <span class="retailer-name">${escapeHtml(retailer.name)}</span>
          ${distanceHTML}
        </span>
        <span class="retailer-item__chevron" aria-hidden="true"></span>
      </span>
    `;

    el.addEventListener('click', () => selectRetailer(index));
    retailerList.appendChild(el);
  }

  // ---- Detail view (shared by the InfoWindow and the mobile dialog) -------
  function buildDetailHTML(retailer, opts) {
    const heading = (opts && opts.headingLevel) || 'h3';
    const addressParts = [retailer.address, retailer.city, retailer.county, retailer.postcode]
      .filter(Boolean).join(', ');
    // opening_hours arrives as HTML from the API; strip tags only to test
    // whether there is any real text before rendering the block.
    const openingText = retailer.opening_hours
      ? String(retailer.opening_hours).replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, '').trim()
      : '';
    const lat = parseFloat(retailer.latitude), lng = parseFloat(retailer.longitude);
    const hasCoords = !isNaN(lat) && !isNaN(lng);

    return `
      <div class="rf-detail">
        <${heading} class="rf-detail__name">${escapeHtml(retailer.name)}</${heading}>
        ${retailer.distance !== undefined
          ? `<p class="rf-detail__distance">${retailer.distance.toFixed(1)} ${DISTANCE_LABEL} away</p>` : ''}
        ${addressParts ? `<p class="rf-detail__address">${escapeHtml(addressParts)}</p>` : ''}
        ${retailer.phone ? `<p class="rf-detail__row"><span class="rf-detail__label">Tel:</span> <a href="tel:${escapeHtml(retailer.phone)}">${escapeHtml(retailer.phone)}</a></p>` : ''}
        ${retailer.email ? `<p class="rf-detail__row"><span class="rf-detail__label">Email:</span> <a href="mailto:${escapeHtml(retailer.email)}">${escapeHtml(retailer.email)}</a></p>` : ''}
        ${retailer.website ? `<p class="rf-detail__row"><span class="rf-detail__label">Web:</span> <a href="${escapeHtml(normalizeUrl(retailer.website))}" target="_blank" rel="noopener">${escapeHtml(retailer.website)}</a></p>` : ''}
        ${openingText ? `<div class="rf-detail__hours"><span class="rf-detail__label">Opening hours:</span><div class="rf-detail__hours-body">${retailer.opening_hours}</div></div>` : ''}
        ${hasCoords ? `<a class="rf-detail__directions" href="https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}" target="_blank" rel="noopener">Get directions</a>` : ''}
      </div>
    `;
  }

  function isMobileLayout() { return mobileQuery.matches; }

  function openInfoWindow(marker, retailer) {
    if (!infoWindow) {
      infoWindow = new google.maps.InfoWindow({ maxWidth: 300 });
      infoWindow.addListener('closeclick', clearSelection);
    }
    infoWindow.setContent(`<div class="rf-infowindow">${buildDetailHTML(retailer)}</div>`);
    infoWindow.open({ map, anchor: marker });
  }

  function openModal(retailer) {
    if (!modal || !modalBody) return;
    modalBody.innerHTML = buildDetailHTML(retailer, { headingLevel: 'h2' });
    if (typeof modal.showModal === 'function') modal.showModal();
    else modal.setAttribute('open', '');
  }

  function closeDetail() {
    if (infoWindow) infoWindow.close();
    if (modal && modal.open) modal.close();
  }

  function clearSelection() {
    selectedIndex = null;
    Array.from(retailerList.getElementsByClassName('retailer-item'))
      .forEach(item => item.classList.remove('active'));
  }

  if (modal) {
    const closeBtn = modal.querySelector('[data-rf-modal-close]');
    if (closeBtn) closeBtn.addEventListener('click', () => modal.close());
    // Clicking the backdrop closes: the dialog fills its own box, so a click
    // landing on the dialog element itself came from outside the content.
    modal.addEventListener('click', (e) => { if (e.target === modal) modal.close(); });
    modal.addEventListener('close', clearSelection);
  }

  // Swapping breakpoint mid-selection would leave the wrong surface open.
  mobileQuery.addEventListener('change', () => {
    if (selectedIndex === null) return;
    closeDetail();
    selectRetailer(selectedIndex);
  });

  // ---- Markers -----------------------------------------------------------
  function updateMarkers() {
    clearMarkers();
    const newMarkers = [];
    retailers.forEach(retailer => {
      const position = { lat: parseFloat(retailer.latitude), lng: parseFloat(retailer.longitude) };
      if (isNaN(position.lat) || isNaN(position.lng)) return;
      const marker = new google.maps.Marker({
        position, map, title: retailer.name, optimized: true,
        icon: {
          path: PIN_SVG_PATH, fillColor: MARKER_COLOR, fillOpacity: 1,
          strokeColor: '#FFFFFF', strokeWeight: 2, scale: 1.2, anchor: new google.maps.Point(0, 30)
        }
      });
      marker.retailerId = retailer.id;
      marker.addListener('click', () => {
        const idx = retailers.findIndex(r => r.id === retailer.id);
        if (idx !== -1) selectRetailer(idx);
      });
      newMarkers.push(marker);
    });
    markers = newMarkers;
    if (userMarker) userMarker.setZIndex(1001);
  }

  function clearMarkers() {
    markers.forEach(m => m.setMap(null));
    markers = [];
    activeMarker = null;
  }

  function highlightMarker(marker) {
    if (activeMarker && activeMarker !== marker && activeMarker.originalIcon) {
      activeMarker.setIcon(activeMarker.originalIcon);
      activeMarker.setZIndex(null);
    }
    if (!marker.originalIcon) marker.originalIcon = { ...marker.getIcon() };
    const highlighted = { ...marker.getIcon(), scale: 1.6, strokeWeight: 3 };
    marker.setIcon(highlighted);
    marker.setZIndex(999);
    activeMarker = marker;
  }

  function selectRetailer(index) {
    const retailer = retailers[index];
    if (!retailer) return;
    selectedIndex = index;
    closeDetail();

    Array.from(retailerList.getElementsByClassName('retailer-item'))
      .forEach(item => item.classList.remove('active'));
    const activeItem = retailerList.querySelector(`[data-index="${index}"]`);
    if (activeItem) {
      activeItem.classList.add('active');
      // Selecting from a map pin should bring the matching card into view.
      activeItem.scrollIntoView({ block: 'nearest' });
    }

    const marker = markers.find(m => m.retailerId === retailer.id);
    if (marker) {
      highlightMarker(marker);
      map.panTo(marker.getPosition());
      // Only zoom in — never yank someone back out if they've zoomed closer.
      if (map.getZoom() < MARKER_ZOOM) map.setZoom(MARKER_ZOOM);
    }

    // No marker means unusable coordinates, so fall back to the dialog rather
    // than silently showing nothing.
    if (isMobileLayout() || !marker) openModal(retailer);
    else openInfoWindow(marker, retailer);
  }

  // ---- Helpers -----------------------------------------------------------
  function saveRecentSearch(query, position) {
    try {
      let recent = JSON.parse(localStorage.getItem('vittoria_recent_searches') || '[]');
      recent.unshift({ query, position });
      if (recent.length > MAX_RECENT_SEARCHES) recent = recent.slice(0, MAX_RECENT_SEARCHES);
      localStorage.setItem('vittoria_recent_searches', JSON.stringify(recent));
    } catch (e) { /* non-critical */ }
  }

  function normalizeUrl(url) {
    if (!url) return '#';
    return /^https?:\/\//i.test(url) ? url : 'https://' + url;
  }

  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
});
